import React, { useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import apinode from '../../services/apinode';
import Swal from 'sweetalert2';
import Navbar from '../../components/navbar/navbar.jsx'


const PacklistDashboard = () => {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [expandedRows, setExpandedRows] = useState(new Set());
    const [selectedOrders, setSelectedOrders] = useState(new Set());

    // Filtros
    const [filterApelido, setFilterApelido] = useState('');
    const [filterDataInicio, setFilterDataInicio] = useState('');
    const [filterDataFim, setFilterDataFim] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [filterImpressao, setFilterImpressao] = useState('');
    const [availableApelidos, setAvailableApelidos] = useState([]);
    const [availableStatus, setAvailableStatus] = useState([]);
    const [bulkLoading, setBulkLoading] = useState(false);

    // Paginação
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    // Função para calcular estatísticas
    const getStatistics = (ordersArray = orders) => {
        const total = ordersArray.length;
        const impressos = ordersArray.filter(order => order.IMP_ETQ_LOCAL === 1).length;
        const naoImpresos = total - impressos;
        
        return { total, impressos, naoImpresos };
    };

    const fetchOrders = async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await apinode.get(`/buscar-envios/${localStorage.idconta}`);
            const ordersData = response.data.data;

            setOrders(ordersData);

            // Extrair apelidos únicos para o filtro
            const uniqueApelidos = [...new Set(ordersData.map(order => order.APELIDO).filter(Boolean))];
            setAvailableApelidos(uniqueApelidos.sort());

            // Extrair status únicos para o filtro
            const uniqueStatus = [...new Set(ordersData.map(order => order.STATUS).filter(Boolean))];
            setAvailableStatus(uniqueStatus.sort());
        } catch (err) {
            setError("Erro ao carregar os dados");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOrders();
    }, []);

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('pt-BR');
    };

    const getStatusBadge = (status) => {
        const statusColors = {
            'Aguardando': 'warning',
            'paid': 'success',
            'Processando': 'info',
            'Enviado': 'primary',
            'Entregue': 'success',
            'Cancelado': 'danger'
        };

        return `badge bg-${statusColors[status] || 'warning'}`;
    };

    const toggleExpanded = (pedidoId) => {
        const newExpanded = new Set(expandedRows);
        if (newExpanded.has(pedidoId)) {
            newExpanded.delete(pedidoId);
        } else {
            newExpanded.add(pedidoId);
        }
        setExpandedRows(newExpanded);
    };

    const handlePrint = (pedidoId, ship) => {
        alert(`Imprimir etiqueta do pedido ${pedidoId} | ship: ${ship}`);
    };

    const handleSelectOrder = (pedidoId) => {
        const newSelected = new Set(selectedOrders);
        if (newSelected.has(pedidoId)) {
            newSelected.delete(pedidoId);
        } else {
            newSelected.add(pedidoId);
        }
        setSelectedOrders(newSelected);
    };

    const handleSelectAll = () => {
        const currentPageOrderIds = paginatedOrders
            .filter(o => o.IMP_ETQ_LOCAL !== 1) // ignora já impressos
            .map(o => o.PEDIDO);
    
        const allCurrentPageSelected = currentPageOrderIds.every(id => selectedOrders.has(id));
    
        if (allCurrentPageSelected) {
            // Desmarcar todos da página atual
            const newSelected = new Set(selectedOrders);
            currentPageOrderIds.forEach(id => newSelected.delete(id));
            setSelectedOrders(newSelected);
        } else {
            // Selecionar todos da página atual (apenas não impressos)
            const newSelected = new Set(selectedOrders);
            currentPageOrderIds.forEach(id => newSelected.add(id));
            setSelectedOrders(newSelected);
        }
    };

    const handleBulkPrint = async () => {
        const toPrint = orders
            .filter(o => selectedOrders.has(o.PEDIDO))
            .map(o => ({ pedido: o.PEDIDO, ship: o.SHIP }));

        if (toPrint.length === 0) {
            Swal.fire({
                icon: 'info',
                title: 'Nenhum pedido selecionado',
                text: 'Selecione ao menos um pedido para imprimir!',
                confirmButtonColor: '#ffc107'
            });
            return;
        }

        try {
            setBulkLoading(true);

            const conta = localStorage.idconta;
            const response = await apinode.post(`/buscar-envios/${conta}`, toPrint);

            Swal.fire({
                icon: 'success',
                title: 'Impressão concluída',
                text: response.data.message || 'Pedidos enviados para impressão!',
                confirmButtonColor: '#198754'
            });
        } catch (error) {
            console.error('Erro ao enviar envios:', error);
            Swal.fire({
                icon: 'error',
                title: 'Erro',
                text: 'Não foi possível enviar os pedidos para impressão.',
                confirmButtonColor: '#dc3545'
            });
        } finally {
            setBulkLoading(false);
            fetchOrders();
        }
    };

    const clearFilters = () => {
        setSearchTerm('');
        setFilterApelido('');
        setFilterDataInicio('');
        setFilterDataFim('');
        setFilterStatus('');
        setFilterImpressao('');
        setSelectedOrders(new Set());
        setCurrentPage(1);
    };

    // Função de filtro
    const filteredOrders = orders?.filter(order => {
        // Filtro por texto (cliente, pedido, CPF)
        const matchesSearch = !searchTerm ||
            order.CLIENTE?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            order.PEDIDO?.toString().includes(searchTerm) ||
            order.CPF?.includes(searchTerm);

        // Filtro por apelido
        const matchesApelido = !filterApelido || order.APELIDO === filterApelido;

        // Filtro por status
        const matchesStatus = !filterStatus || order.STATUS === filterStatus;

        // Filtro por impressão
        const matchesImpressao = 
            filterImpressao === '' || 
            (filterImpressao === 'impresso' && order.IMP_ETQ_LOCAL === 1) ||
            (filterImpressao === 'nao_impresso' && order.IMP_ETQ_LOCAL !== 1);

        // Filtro por data de despacho (BUFFERING_DATE)
        let matchesDate = true;
        if (filterDataInicio || filterDataFim) {
            const bufferingDate = order.BUFFERING_DATE ? new Date(order.BUFFERING_DATE) : null;

            if (bufferingDate) {
                const dataInicio = filterDataInicio ? new Date(filterDataInicio) : null;
                const dataFim = filterDataFim ? new Date(filterDataFim + 'T23:59:59') : null;

                matchesDate = (!dataInicio || bufferingDate >= dataInicio) &&
                    (!dataFim || bufferingDate <= dataFim);
            } else {
                matchesDate = false;
            }
        }

        return matchesSearch && matchesApelido && matchesStatus && matchesImpressao && matchesDate;
    }) || [];

    // Estatísticas dos pedidos filtrados
    const filteredStats = getStatistics(filteredOrders);

    // Paginação
    const totalPages = Math.ceil(filteredOrders.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginatedOrders = filteredOrders.slice(startIndex, startIndex + itemsPerPage);

    // Verifica se todos da página atual estão selecionados
    const allCurrentPageSelected = paginatedOrders.length > 0 &&
        paginatedOrders.every(order => selectedOrders.has(order.PEDIDO));

    const someCurrentPageSelected = paginatedOrders.some(order => selectedOrders.has(order.PEDIDO));

    // Reset página quando filtros mudam
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, filterApelido, filterDataInicio, filterDataFim, filterStatus, filterImpressao]);

    const renderPagination = () => {
        if (totalPages <= 1) return null;

        const pages = [];
        const maxVisiblePages = 5;

        let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

        if (endPage - startPage < maxVisiblePages - 1) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        return (
            <nav aria-label="Paginação">
                <ul className="pagination justify-content-center">
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button
                            className="page-link"
                            onClick={() => setCurrentPage(1)}
                            disabled={currentPage === 1}
                        >
                            ««
                        </button>
                    </li>
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button
                            className="page-link"
                            onClick={() => setCurrentPage(currentPage - 1)}
                            disabled={currentPage === 1}
                        >
                            ‹
                        </button>
                    </li>

                    {startPage > 1 && (
                        <>
                            <li className="page-item">
                                <button className="page-link" onClick={() => setCurrentPage(1)}>1</button>
                            </li>
                            {startPage > 2 && <li className="page-item disabled"><span className="page-link">...</span></li>}
                        </>
                    )}

                    {Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i).map(page => (
                        <li key={page} className={`page-item ${currentPage === page ? 'active' : ''}`}>
                            <button
                                className="page-link"
                                onClick={() => setCurrentPage(page)}
                            >
                                {page}
                            </button>
                        </li>
                    ))}

                    {endPage < totalPages && (
                        <>
                            {endPage < totalPages - 1 && <li className="page-item disabled"><span className="page-link">...</span></li>}
                            <li className="page-item">
                                <button className="page-link" onClick={() => setCurrentPage(totalPages)}>{totalPages}</button>
                            </li>
                        </>
                    )}

                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                        <button
                            className="page-link"
                            onClick={() => setCurrentPage(currentPage + 1)}
                            disabled={currentPage === totalPages}
                        >
                            ›
                        </button>
                    </li>
                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                        <button
                            className="page-link"
                            onClick={() => setCurrentPage(totalPages)}
                            disabled={currentPage === totalPages}
                        >
                            »»
                        </button>
                    </li>
                </ul>
            </nav>
        );
    };

    if (loading) {
        return (
            <div className="container-fluid mt-4 text-center">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Carregando...</span>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="container-fluid mt-4">
                <div className="alert alert-danger" role="alert">
                    {error}
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="container-fluid mt-2">
                {/* Barra de filtros expandida */}
                <div className="row mb-3">
                    <div className="col-md-2">
                        <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="Cliente, pedido ou CPF..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <div className="col-md-2">
                        <select
                            className="form-select form-select-sm"
                            value={filterApelido}
                            onChange={(e) => setFilterApelido(e.target.value)}
                        >
                            <option value="">Todas as contas</option>
                            {availableApelidos.map(apelido => (
                                <option key={apelido} value={apelido}>
                                    {apelido}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="col-md-2">
                        <select
                            className="form-select form-select-sm"
                            value={filterStatus}
                            onChange={(e) => setFilterStatus(e.target.value)}
                        >
                            <option value="">Todos os status</option>
                            {availableStatus.map(status => (
                                <option key={status} value={status}>
                                    {status}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="col-md-2">
                        <select
                            className="form-select form-select-sm"
                            value={filterImpressao}
                            onChange={(e) => setFilterImpressao(e.target.value)}
                        >
                            <option value="">Todas impressões</option>
                            <option value="impresso">Apenas impressos</option>
                            <option value="nao_impresso">Não impressos</option>
                        </select>
                    </div>
                    <div className="col-md-1">
                        <input
                            type="date"
                            className="form-control form-control-sm"
                            title="Data início"
                            value={filterDataInicio}
                            onChange={(e) => setFilterDataInicio(e.target.value)}
                        />
                    </div>
                    <div className="col-md-1">
                        <input
                            type="date"
                            className="form-control form-control-sm"
                            title="Data fim"
                            value={filterDataFim}
                            onChange={(e) => setFilterDataFim(e.target.value)}
                        />
                    </div>
                    <div className="col-md-2">
                        <div className="d-flex gap-1">
                            <button className="btn btn-outline-secondary btn-sm" onClick={clearFilters}>
                                Limpar
                            </button>
                            <button
                                className="btn btn-warning btn-sm"
                                onClick={handleBulkPrint}
                                disabled={selectedOrders.size === 0 || bulkLoading}
                            >
                                {bulkLoading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2"></span>
                                        Imprimindo...
                                    </>
                                ) : (
                                    <>Imprimir ({selectedOrders.size})</>
                                )}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Estatísticas e contador */}
                <div className="row mb-3">
                    <div className="col-md-8">
                        <div className="d-flex gap-2 flex-wrap">
                            <span className="badge bg-info fs-6">
                                Total: {filteredStats.total}
                            </span>
                            <span className="badge bg-success fs-6">
                                Impressos: {filteredStats.impressos}
                            </span>
                            <span className="badge bg-warning fs-6">
                                Não impressos: {filteredStats.naoImpresos}
                            </span>
                            {selectedOrders.size > 0 && (
                                <span className="badge bg-primary fs-6">
                                    Selecionados: {selectedOrders.size}
                                </span>
                            )}
                        </div>
                    </div>
                    <div className="col-md-4 text-end">
                        <div className="d-flex justify-content-end align-items-center gap-2">
                            <span className="small text-muted">Itens por página:</span>
                            <select
                                className="form-select form-select-sm"
                                style={{ width: 'auto' }}
                                value={itemsPerPage}
                                onChange={(e) => {
                                    setItemsPerPage(Number(e.target.value));
                                    setCurrentPage(1);
                                }}
                            >
                                <option value={10}>10</option>
                                <option value={25}>25</option>
                                <option value={50}>50</option>
                                <option value={100}>100</option>
                            </select>
                        </div>
                    </div>
                </div>

                {filteredOrders.length === 0 ? (
                    <div className="text-center py-5">
                        <i className="bi bi-inbox display-1 text-muted"></i>
                        <h4 className="text-muted mt-3">Nenhum pedido encontrado</h4>
                        {(searchTerm || filterApelido || filterDataInicio || filterDataFim || filterStatus || filterImpressao) && (
                            <button className="btn btn-link" onClick={clearFilters}>
                                Limpar filtros
                            </button>
                        )}
                    </div>
                ) : (
                    <>
                        <Navbar />
                        {/* Checkbox selecionar todos da página */}
                        <div className="mb-2">
                            <div className="form-check">
                                <input
                                    type="checkbox"
                                    className="form-check-input"
                                    checked={allCurrentPageSelected}
                                    ref={(input) => {
                                        if (input) {
                                            input.indeterminate = someCurrentPageSelected && !allCurrentPageSelected;
                                        }
                                    }}
                                    onChange={handleSelectAll}
                                />
                                <label className="form-check-label small text-muted">
                                    Selecionar todos não impressos desta página ({paginatedOrders.filter(o => o.IMP_ETQ_LOCAL !== 1).length} disponíveis)
                                </label>
                            </div>
                        </div>

                        {/* Lista de pedidos */}
                        <div className="border rounded">
                            {paginatedOrders.map((order, index) => (
                                <div
                                    key={order.PEDIDO}
                                    className={`p-3 ${index > 0 ? 'border-top' : ''} ${selectedOrders.has(order.PEDIDO) ? 'bg-light' : ''}`}
                                >
                                    <div className="row align-items-center">
                                        {/* Checkbox + Pedido */}
                                        <div className="col-md-2">
                                            <div className="d-flex align-items-center">
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input me-2"
                                                    checked={selectedOrders.has(order.PEDIDO)}
                                                    disabled={order.IMP_ETQ_LOCAL === 1}
                                                    onChange={() => handleSelectOrder(order.PEDIDO)}
                                                />
                                                <button
                                                    className="btn btn-link p-0 me-2"
                                                    onClick={() => toggleExpanded(order.PEDIDO)}
                                                >
                                                    <i className={`bi bi-chevron-${expandedRows.has(order.PEDIDO) ? 'down' : 'right'} text-primary`}></i>
                                                </button>
                                                <div>
                                                    <div className="fw-bold text-primary">#{order.PEDIDO}</div>
                                                    <div className="small text-muted">{formatDate(order.DATA)}</div>
                                                    <span className="badge bg-secondary">{order.APELIDO}</span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Cliente */}
                                        <div className="col-md-3">
                                            <div className="fw-bold">{order.CLIENTE}</div>
                                            <div className="small text-muted">
                                                {order.CPF && `CPF: ${order.CPF}`}
                                            </div>
                                            {order.BUFFERING_DATE && (
                                                <div className="small text-info">
                                                    <i className="bi bi-calendar me-1"></i>
                                                    Despacho: {formatDate(order.BUFFERING_DATE)}
                                                </div>
                                            )}
                                        </div>

                                        {/* Status e Itens */}
                                        <div className="col-md-2">
                                            <span className={getStatusBadge(order.STATUS)}>
                                                {order.STATUS}
                                            </span>
                                            <div className="small text-muted">
                                                Itens: {order.ITEM_CONFERIDOS}/{order.TOTAIS_ITENS}
                                            </div>
                                        </div>

                                        {/* Situação */}
                                        <div className="col-md-2">
                                            <div className="small text-muted">Situação:</div>
                                            <span className={`badge bg-${order.SITUACAO === 'paid' ? 'success' : 'warning'}`}>
                                                {order.SITUACAO === 'paid' ? 'Pago' : order.SITUACAO}
                                            </span>
                                        </div>

                                        {/* CEP */}
                                        <div className="col-md-2">
                                            <div className="small text-muted">CEP:</div>
                                            <div className="fw-bold">{order.ENT_CEP || '-'}</div>
                                        </div>

                                        {/* Ações */}
                                        <div className="col-md-1">
                                            {order.IMP_ETQ_LOCAL === 1 ? (
                                                <span className="badge bg-success small">
                                                    <i className="bi bi-check"></i> Impresso
                                                </span>
                                            ) : (
                                                <button
                                                    className="btn btn-outline-warning btn-sm"
                                                    onClick={() => handlePrint(order.PEDIDO, order.SHIP)}
                                                >
                                                    Imprimir
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    {/* Detalhes expandidos */}
                                    {expandedRows.has(order.PEDIDO) && (
                                        <div className="mt-3 pt-3 border-top bg-white rounded p-2">
                                            <div className="row">
                                                <div className="col-md-6">
                                                    <h6 className="text-secondary mb-2">Informações Adicionais</h6>
                                                    <div className="small">
                                                        <div><strong>ID Loja:</strong> {order.ID_LOJA}</div>
                                                        <div><strong>ID Pedido Loja:</strong> {order.IDPEDIDOLOJA}</div>
                                                        <div><strong>Integrado:</strong> {order.INTEGRADO}</div>
                                                        <div><strong>Status Despacho:</strong> {order.STATUS_DESPACHO}</div>
                                                        <div><strong>Código Envio:</strong> {order.SHIP}</div>
                                                    </div>
                                                </div>
                                                <div className="col-md-6">
                                                    <h6 className="text-secondary mb-2">Endereço</h6>
                                                    <div className="small">
                                                        <div><strong>CEP:</strong> {order.ENT_CEP}</div>
                                                        {order.END_ENT && (
                                                            <div className="mt-2">
                                                                <div className="bg-light p-2 rounded">
                                                                    {order.END_ENT.replace(/\r\n/g, ', ')}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>

                        {/* Paginação */}
                        <div className="mt-4 d-flex justify-content-between align-items-center">
                            <div className="small text-muted">
                                Mostrando {startIndex + 1} a {Math.min(startIndex + itemsPerPage, filteredOrders.length)} de {filteredOrders.length} pedidos
                            </div>
                            {renderPagination()}
                        </div>
                    </>
                )}
            </div>
        </>
    );
};

export default PacklistDashboard;